// @erp/ui/route-progress — the 2px accent bar under the header that says "your
// click was taken, the page is coming".
//
// WHY IT EXISTS (audit UX-01, 2026-09-16). A sidebar click left the outlet
// EMPTY for the full API latency — the trace read: URL changed at 7ms, outlet
// empty from 90ms to 5158ms behind a 5s API delay — and nothing on screen said
// the click had registered, so people clicked again. The standard signal is a
// thin indeterminate bar on the chrome's bottom edge; it needs the shell alone,
// so it lives here, ui-internal, and AppShell mounts it under the Header.
//
// WHAT IT WATCHES: two things, either of which means "working" —
//   · the router's `isLoading` (a route match is pending: its lazy chunk, its
//     loader, its redirect) — `useRouterState({ select: s => s.isLoading })`;
//   · solid-query's fetch count (`useIsFetching()`), so a screen whose chrome
//     landed but whose data is still on the wire keeps the bar up — the audit
//     case, where the route resolved in 90ms and the screen sat on a skeleton
//     for five seconds. Read only when a QueryClient is in scope: the catalog
//     mounts no QueryClientProvider, and a bar that threw there would be a bar
//     no one could review.
//
// THE DELAY (`delayMs`, 150 by default): a transition that finishes inside it
// shows nothing. Most cached navigations do, and a bar that flashes for 40ms on
// every click is noise, not feedback. Once shown it stays until BOTH sources
// are idle. 150ms is the classic perceptual budget for "instant" (Nielsen /
// Card's 0.1s tier with a little slack for the paint) — HOUSE, Spectrum's
// progress bar has no show-delay of its own.
//
// THE SURFACE, cited:
//   height   2px = --spectrum-border-width-200, the stroke Spectrum's tabs use
//            for their selection indicator (spectrum.css §6). Spectrum's
//            progress BAR ramp starts at 4px (--spectrum-progress-bar-
//            thickness-small); a bar riding the header's edge is chrome, not a
//            component, and takes the chrome stroke — HOUSE, recorded.
//   fill     bg-primary — progress is accent-only (DEV-GUIDE-002 §12.3 / §12.4
//            "Progress — accent only"); 70% of the track wide
//            (--spectrum-progressbar-fill-size-indeterminate).
//   motion   `animate-sweep` — base.css mints Adobe's indeterminate-loop:
//            1000ms (--spectrum-animation-duration-2000) on
//            cubic-bezier(0.45, 0, 0.4, 1) (--spectrum-animation-ease-in-out).
//            `motion-reduce:animate-none motion-reduce:w-full` makes it a
//            still full-width bar under prefers-reduced-motion — the state is
//            still shown, only the movement is not.
//   layout   the wrapper is `relative h-0`: it takes NO height in the shell's
//            column, so the bar appearing and vanishing never shifts <main>;
//            the bar itself is absolutely positioned over main's top edge,
//            above the sticky filter band (z-30 > the band's z-20).
//   a11y     role="progressbar" with aria-label "Loading" and no aria-valuenow
//            (indeterminate, by the ARIA spec); aria-hidden while idle is moot
//            because the bar is not rendered at all then.
//
// TWO EXPORTS. `RouteProgressBar` is the presentational bar driven by an
// `active` prop — the demo's knob, the tests' fake timers, and any surface
// that has its own notion of pending. `RouteProgress` wires it to the router
// and the query cache; it is what the shell mounts.
import { Show, createEffect, createSignal, on, onCleanup, useContext } from 'solid-js';
import { useRouterState } from '@tanstack/solid-router';
import { QueryClientContext, useIsFetching } from '@tanstack/solid-query';

export interface RouteProgressBarProps {
  /** Something is in flight. The bar appears `delayMs` after this turns true and leaves the moment it turns false. */
  active: boolean;
  /** How long a wait must last before the bar shows, in ms — a transition that finishes sooner shows nothing. Default 150. */
  delayMs?: number;
  /** The accessible name — what a screen reader hears while the bar is up. Default "Loading". */
  label?: string;
}

/**
 * The presentational bar: `active` in, a 2px accent sweep out after the
 * delay. RouteProgress drives it from the router and the query cache; drive
 * it yourself for a surface with its own pending signal.
 */
export function RouteProgressBar(props: RouteProgressBarProps) {
  const [visible, setVisible] = createSignal(false);
  createEffect(
    on(
      () => props.active,
      (active) => {
        if (!active) {
          setVisible(false);
          return;
        }
        // The delay: cached navigations finish inside it and show nothing.
        const t = setTimeout(() => setVisible(true), props.delayMs ?? 150);
        onCleanup(() => clearTimeout(t));
      },
    ),
  );
  return (
    <div class="relative z-30 h-0" data-route-progress={visible() ? 'active' : 'idle'}>
      <Show when={visible()}>
        <div
          role="progressbar"
          aria-label={props.label ?? 'Loading'}
          class="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden"
        >
          <div class="h-full w-[70%] animate-sweep bg-primary motion-reduce:w-full motion-reduce:animate-none" />
        </div>
      </Show>
    </div>
  );
}

/**
 * The shell's route progress: a 2px accent sweep under the header while the
 * router is loading a match OR any solid-query request is in flight, shown
 * only once the wait has lasted `delayMs`. Composed inside AppShell; a portal
 * never mounts it directly.
 */
export function RouteProgress(props: { delayMs?: number }) {
  const routerLoading = useRouterState({ select: (s) => s.isLoading });
  // The catalog (and any test) may mount the shell without a QueryClient —
  // then the router is the only source, rather than a thrown "No QueryClient".
  const client = useContext(QueryClientContext);
  const fetching = client ? useIsFetching(undefined, client) : () => 0;
  const active = () => routerLoading() || fetching() > 0;
  return <RouteProgressBar active={active()} delayMs={props.delayMs} />;
}
